<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { ArrowRightLeft, Plus, Trash2, RefreshCw, MessageSquare, User, Box } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface SeqEntity {
  id: string
  type: 'actor' | 'participant'
}

interface SeqAction {
  type: 'message' | 'note'
  from: string
  to: string
  arrow: string
  text: string
  notePosition: 'left of' | 'right of' | 'over'
}

// Estado visual
const autonumber = ref(true)
const entities = ref<SeqEntity[]>([
  { id: 'Cliente', type: 'actor' },
  { id: 'Servidor', type: 'participant' },
  { id: 'BaseDeDatos', type: 'participant' }
])

const actions = ref<SeqAction[]>([
  { type: 'message', from: 'Cliente', arrow: '->>', to: 'Servidor', text: 'Solicitar datos', notePosition: 'over' },
  { type: 'message', from: 'Servidor', arrow: '->>', to: 'BaseDeDatos', text: 'Consulta SQL', notePosition: 'over' },
  { type: 'message', from: 'BaseDeDatos', arrow: '-->>', to: 'Servidor', text: 'Resultados', notePosition: 'over' },
  { type: 'note', from: 'Servidor', arrow: '->>', to: 'Servidor', text: 'Procesa la información', notePosition: 'right of' },
  { type: 'message', from: 'Servidor', arrow: '-->>', to: 'Cliente', text: 'Respuesta JSON', notePosition: 'over' }
])

const arrowTypes = [
  { value: '->>', label: 'Sólida con flecha (->>)' },
  { value: '-->>', label: 'Punteada con flecha (-->>)' },
  { value: '->', label: 'Sólida sin flecha (->)' },
  { value: '-->', label: 'Punteada sin flecha (-->)' },
  { value: '-x', label: 'Sólida cruzada (-x)' },
  { value: '--x', label: 'Punteada cruzada (--x)' }
]

// Acciones Entidades
const addEntity = () => {
  entities.value.push({ id: `Entidad_${entities.value.length + 1}`, type: 'participant' })
}
const removeEntity = (idx: number) => {
  entities.value.splice(idx, 1)
}

// Acciones Mensajes
const addMessage = () => {
  const from = entities.value.length > 0 ? entities.value[0].id : 'A'
  const to = entities.value.length > 1 ? entities.value[1].id : from
  actions.value.push({ type: 'message', from, arrow: '->>', to, text: 'Nuevo mensaje', notePosition: 'over' })
}

const addNote = () => {
  const from = entities.value.length > 0 ? entities.value[0].id : 'A'
  actions.value.push({ type: 'note', from, arrow: '->>', to: from, text: 'Nueva nota', notePosition: 'over' })
}

const removeAction = (idx: number) => {
  actions.value.splice(idx, 1)
}

const moveActionUp = (idx: number) => {
  if (idx > 0) {
    const temp = actions.value[idx]
    actions.value[idx] = actions.value[idx - 1]
    actions.value[idx - 1] = temp
  }
}

const moveActionDown = (idx: number) => {
  if (idx < actions.value.length - 1) {
    const temp = actions.value[idx]
    actions.value[idx] = actions.value[idx + 1]
    actions.value[idx + 1] = temp
  }
}

// Generar Mermaid
const generateMermaid = () => {
  let code = `sequenceDiagram\n`
  
  if (autonumber.value) {
    code += `    autonumber\n`
  }

  entities.value.forEach(e => {
    if (e.id.trim()) {
      code += `    ${e.type} ${e.id.trim()}\n`
    }
  })

  actions.value.forEach(act => {
    const safeFrom = act.from.trim()
    const safeTo = act.to.trim()
    
    if (act.type === 'message') {
      if (safeFrom && safeTo) {
        code += `    ${safeFrom}${act.arrow}${safeTo}: ${act.text}\n`
      }
    } else if (act.type === 'note') {
      if (safeFrom && safeTo && safeFrom !== safeTo) {
        code += `    Note ${act.notePosition} ${safeFrom},${safeTo}: ${act.text}\n`
      } else if (safeFrom) {
        code += `    Note ${act.notePosition} ${safeFrom}: ${act.text}\n`
      }
    }
  })
  
  editableCode.value = code
}

// Sincronizar Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newEntities: SeqEntity[] = []
  const newActions: SeqAction[] = []
  let newAutonumber = false
  
  const entityRegex = /^(actor|participant)\s+(.+)$/
  const noteRegex = /^Note\s+(left of|right of|over)\s+([^:]+):\s*(.+)$/
  const msgRegex = /^([^-]+)\s*(-+(?:x|>>|>))\s*([^:]+)\s*:\s*(.+)$/

  for (const line of lines) {
    const t = line.trim()
    if (!t || t === 'sequenceDiagram') continue

    if (t === 'autonumber') {
      newAutonumber = true
    } else if (entityRegex.test(t)) {
      const match = t.match(entityRegex)!
      // Si tiene alias (Participant A as B), no lo soportamos perfecto en visual, pero tomaremos todo
      newEntities.push({
        type: match[1] as 'actor' | 'participant',
        id: match[2].trim()
      })
    } else if (noteRegex.test(t)) {
      const match = t.match(noteRegex)!
      const pos = match[1] as 'left of' | 'right of' | 'over'
      const targets = match[2].split(',').map(s => s.trim())
      newActions.push({
        type: 'note',
        notePosition: pos,
        from: targets[0],
        to: targets.length > 1 ? targets[1] : targets[0],
        arrow: '->>',
        text: match[3].trim()
      })
    } else if (msgRegex.test(t)) {
      const match = t.match(msgRegex)!
      newActions.push({
        type: 'message',
        from: match[1].trim(),
        arrow: match[2].trim(),
        to: match[3].trim(),
        text: match[4].trim(),
        notePosition: 'over'
      })
    }
  }

  autonumber.value = newAutonumber
  
  // No sobreescribir entidades si no hay ninguna explícita, porque Mermaid las infiere
  if (newEntities.length > 0) {
    entities.value = newEntities
  }
  
  if (newActions.length > 0) {
    actions.value = newActions
    
    // Si no declararon entidades explícitas, inferirlas de los mensajes
    if (newEntities.length === 0) {
      const inferred = new Set<string>()
      newActions.forEach(a => {
        if (a.from) inferred.add(a.from)
        if (a.to) inferred.add(a.to)
      })
      entities.value = Array.from(inferred).map(id => ({ id, type: 'participant' }))
    }
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([autonumber, entities, actions], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Secuencia"
    description="Modela el flujo de mensajes e interacciones entre sistemas, objetos o roles a lo largo del tiempo."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <ArrowRightLeft class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addMessage"><MessageSquare class="w-4 h-4 mr-2" /> Añadir Mensaje</Button>
      <Button size="sm" variant="secondary" @click="addNote"><Box class="w-4 h-4 mr-2" /> Añadir Nota</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Participantes -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4">
          <div class="flex items-center justify-between border-b pb-2">
            <h3 class="font-semibold text-lg">Participantes / Actores</h3>
            <div class="flex items-center gap-2">
              <input type="checkbox" id="autonumber" v-model="autonumber" class="w-4 h-4 text-primary rounded" />
              <label for="autonumber" class="text-sm cursor-pointer select-none">Auto-numerar mensajes</label>
            </div>
          </div>

          <div class="flex flex-wrap gap-3">
            <div 
              v-for="(entity, idx) in entities" 
              :key="idx"
              class="flex items-center gap-2 bg-muted p-2 rounded-md border group"
            >
              <select v-model="entity.type" class="bg-background border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1">
                <option value="actor">Actor</option>
                <option value="participant">Caja</option>
              </select>
              <input 
                v-model="entity.id" 
                class="bg-transparent border-b border-transparent focus:border-primary px-1 py-1 text-sm font-semibold w-24 focus:outline-none"
                placeholder="Nombre"
              />
              <button 
                @click="removeEntity(idx)" 
                class="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
            
            <Button variant="outline" size="sm" class="border-dashed" @click="addEntity">
              <Plus class="w-4 h-4" />
            </Button>
          </div>
        </div>

        <!-- Flujo de Secuencia -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4 flex-1">
          <h3 class="font-semibold text-lg border-b pb-2">Flujo de Interacción</h3>
          
          <div class="flex flex-col gap-2">
            <div 
              v-for="(action, idx) in actions" 
              :key="idx"
              class="flex items-center gap-2 bg-background border p-2 rounded-md shadow-sm relative group"
            >
              <!-- Order Controls -->
              <div class="flex flex-col gap-1 pr-2 border-r shrink-0">
                <button @click="moveActionUp(idx)" :disabled="idx === 0" class="text-muted-foreground hover:text-primary disabled:opacity-30">▲</button>
                <button @click="moveActionDown(idx)" :disabled="idx === actions.length - 1" class="text-muted-foreground hover:text-primary disabled:opacity-30">▼</button>
              </div>
              
              <!-- Message Config -->
              <div v-if="action.type === 'message'" class="flex-1 flex flex-wrap items-center gap-2 pl-2">
                <MessageSquare class="w-4 h-4 text-blue-500 shrink-0" />
                <select v-model="action.from" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none min-w-[100px]">
                  <option v-for="e in entities" :key="e.id" :value="e.id">{{ e.id }}</option>
                </select>
                
                <select v-model="action.arrow" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none">
                  <option v-for="arr in arrowTypes" :key="arr.value" :value="arr.value">{{ arr.label }}</option>
                </select>
                
                <select v-model="action.to" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none min-w-[100px]">
                  <option v-for="e in entities" :key="e.id" :value="e.id">{{ e.id }}</option>
                </select>

                <input 
                  v-model="action.text" 
                  class="flex-1 min-w-[150px] border rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  placeholder="Mensaje..."
                />
              </div>

              <!-- Note Config -->
              <div v-else class="flex-1 flex flex-wrap items-center gap-2 pl-2">
                <Box class="w-4 h-4 text-amber-500 shrink-0" />
                <span class="text-xs font-bold uppercase text-muted-foreground">Nota</span>
                
                <select v-model="action.notePosition" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none">
                  <option value="left of">Izquierda de</option>
                  <option value="right of">Derecha de</option>
                  <option value="over">Sobre</option>
                </select>
                
                <select v-model="action.from" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none min-w-[100px]">
                  <option v-for="e in entities" :key="e.id" :value="e.id">{{ e.id }}</option>
                </select>
                
                <span class="text-xs text-muted-foreground" v-if="action.notePosition === 'over'">y</span>
                <select v-if="action.notePosition === 'over'" v-model="action.to" class="bg-muted border rounded px-2 py-1 text-xs focus:outline-none min-w-[100px]">
                  <option v-for="e in entities" :key="e.id" :value="e.id">{{ e.id }}</option>
                </select>

                <input 
                  v-model="action.text" 
                  class="flex-1 min-w-[150px] border rounded px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-amber-500/10"
                  placeholder="Contenido de la nota..."
                />
              </div>

              <button 
                @click="removeAction(idx)" 
                class="text-muted-foreground hover:bg-destructive hover:text-white p-2 rounded-full transition-colors shrink-0"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
            
            <div v-if="actions.length === 0" class="text-center text-muted-foreground py-8 border-2 border-dashed rounded-md">
              Aún no hay interacciones.
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
